/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Button, Form, Input, Modal, Radio, Select, Space, Steps, Tooltip, message, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { FormInput } from "../../../components/ui/UserInteraction";
import { logOutUser } from '../../../common';
import { DOMAIN_ID, ORG_ID } from '../../../constants';
import { isEmpty } from '../../../utils/helpers';
import { dispatchAdDomain, dispatchGetAllDoamins } from '../../../utils/apiservice';
import { AN_COLORS, AN_FONT, AN_MONO } from '../../../design-system/tokens';
import GatewayInstall from './GatewayInstall';

const deriveBaseDn = (domain) =>
  (domain || "").split(".").filter(Boolean).map((p) => `DC=${p}`).join(",");

const validateGroupChips = (_, chips = []) => {
  for (const raw of chips) {
    const v = (raw || "").trim();
    if (!v) continue;
    if (v.includes(",")) {
      return Promise.reject(new Error(`"${raw}" contains a comma — split it into separate chips`));
    }
    if (/^cn=/i.test(v)) {
      return Promise.reject(new Error(`"${raw}" should be a plain name, not a CN= DN`));
    }
  }
  return Promise.resolve();
};

const validateOuChips = (_, chips = []) => {
  for (const raw of chips) {
    const v = (raw || "").trim();
    if (!v) continue;
    if (v.includes(",")) {
      return Promise.reject(new Error(`"${raw}" contains a comma — split it into separate chips`));
    }
    if (!/^ou=/i.test(v)) {
      return Promise.reject(new Error(`"${raw}" must start with OU=`));
    }
  }
  return Promise.resolve();
};

const RadioChoice = ({ value, title, hint }) => (
  <Radio value={value} style={{ display: "flex", alignItems: "flex-start", marginBottom: 10 }}>
    <div style={{ lineHeight: 1.4 }}>
      <div style={{ fontSize: 13, fontWeight: 500 }}>{title}</div>
      <div style={{ fontSize: 12.5, color: AN_COLORS.muted }}>{hint}</div>
    </div>
  </Radio>
);

export default function IdentityAD() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { Text } = Typography;
  const [notification, setNotification] = message.useMessage();

  const [current, setCurrent] = useState(0);
  const [adId, setAdId] = useState("");
  const [formVal, setFormVal] = useState(null);
  const [existingDomains, setExistingDomains] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(false);

  const handleApiFailure = ({ message: msg, isLogout = false }) => {
    setIsDataLoading(false);
    notification.error({ content: msg });
    if (isLogout) logOutUser();
  };

  const handleApiError = ({ message: msg }) => {
    setIsDataLoading(false);
    notification.error({ content: msg });
  };

  const fetchExistingDomains = () => {
    const payload = {
      tenantId: DOMAIN_ID(),
      filter: "",
      orgId: ORG_ID(),
    };
    dispatchGetAllDoamins({
      payload,
      onSuccess: (res) => setExistingDomains(res?.domains || []),
      onFailure: handleApiFailure,
      onError: handleApiError,
    });
  };

  const buildPayload = (e) => {
    const parts = (e.domain || "").split(".").filter(Boolean);
    const dc1 = parts[0] || "";
    const dc2 = parts.slice(1).join(".") || "";
    const derivedDc = parts.map((p) => `DC=${p}`).join(",");
    const dcString = (e.baseDn && e.baseDn.trim()) || derivedDc;

    const syncFilterGroups = (e.syncFilterGroups || [])
      .map((s) => (s || "").trim())
      .filter(Boolean)
      .join(",");

    const syncFilterOUs = (e.syncFilterOUs || [])
      .map((s) => (s || "").trim())
      .filter(Boolean)
      .join(",");

    return {
      directoryName: e.directoryName,
      domain: e.domain,
      dc: dcString,
      dc1,
      dc2,
      identitySource: "AD",
      integrationType: "Agent",
      enforcementMode: e.enforcementMode,
      fallbackMode: e.fallbackMode,
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      status: "Pending",
      syncFilterGroups,
      syncFilterOUs,
      // The DC sensor runs on the DC and binds as its own machine account, so
      // these are no longer collected. Sent as inert defaults for API compatibility.
      adHost: "",
      SSLconfigured: "No",
      Port: 389,
      userName: "",
      adAdminUserName: "",
    };
  };

  const submitDomain = (payload) => {
    setIsDataLoading(true);
    dispatchAdDomain({
      payload,
      onSuccess: (res) => {
        setIsDataLoading(false);
        const id = res?.id;
        setAdId(id);
        setFormVal(payload);
        setExistingDomains((prev) => {
          if (prev.some((d) => String(d.id) === String(id))) return prev;
          return [...prev, { ...payload, id }];
        });
        setCurrent(1);
      },
      onFailure: handleApiFailure,
      onError: handleApiError,
    });
  };

  const handleConnectADSubmit = (e) => {
    const payload = buildPayload(e);

    if (isEmpty(payload.dc1) || isEmpty(payload.dc2)) {
      notification.error({ content: "Please enter a valid domain (e.g. authnull.lab)" });
      return;
    }

    const duplicate = existingDomains.find(
      (d) => d.dc1 === payload.dc1 && d.dc2 === payload.dc2
    );

    if (duplicate) {
      Modal.confirm({
        title: "Duplicate Active Directory",
        content: "An AD with this domain already exists. Do you want to continue?",
        okText: "Continue",
        cancelText: "Cancel",
        onOk: () => submitDomain(payload),
      });
      return;
    }

    submitDomain(payload);
  };

  useEffect(() => {
    fetchExistingDomains();
  }, []);

  const ActiveDirectory = () => {
    const [baseDnEditable, setBaseDnEditable] = useState(false);
    const [scopeNarrowed, setScopeNarrowed] = useState(false);

    return (
      <Row>
        <Col className="p-4" span={24}>
          <Row>
            <Col span={24}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleConnectADSubmit}
                initialValues={{
                  enforcementMode: "monitor",
                  fallbackMode: "allow",
                }}
                onValuesChange={(changedValues) => {
                  if (changedValues.domain !== undefined && !baseDnEditable) {
                    form.setFieldsValue({ baseDn: deriveBaseDn(changedValues.domain) });
                  }
                }}
              >
                <Row justify="space-between" align="middle">
                  <Col span={11}>
                    <FormInput
                      name="directoryName"
                      label="Display Name"
                      rules={[{ required: true, message: "Display Name is required" }]}
                      placeholder="My AD"
                    />
                  </Col>
                  <Col span={11}>
                    <FormInput
                      name="domain"
                      label="Domain Name"
                      extraInfo='Domain must contain a dot and no @ symbol, e.g. "authnull.lab"'
                      rules={[
                        { required: true, message: "Domain Name is required" },
                        {
                          validator: (_, v) => {
                            if (!v) return Promise.resolve();
                            if (v.includes("@")) return Promise.reject(new Error("Domain cannot contain @"));
                            if (!v.includes(".")) return Promise.reject(new Error("Domain must contain a dot"));
                            return Promise.resolve();
                          },
                        },
                      ]}
                      placeholder="authnull.lab"
                    />
                  </Col>

                  <Col span={11}>
                    <Form.Item
                      className="mb-2"
                      label={
                        <Space size={4}>
                          <span>Base DN</span>
                          <Button
                            type="link"
                            size="small"
                            className="!p-0 !h-auto"
                            onClick={() => setBaseDnEditable((v) => !v)}
                          >
                            {baseDnEditable ? "Reset" : "Edit"}
                          </Button>
                        </Space>
                      }
                      name="baseDn"
                      tooltip={baseDnEditable ? undefined : "Auto-derived from domain"}
                    >
                      <Input
                        disabled={!baseDnEditable}
                        placeholder="DC=authnull,DC=lab"
                        className="!rounded-md max-h-8 px-2 !text-sm !border border-black/50"
                        onBlur={() => {
                          if (baseDnEditable && !form.getFieldValue("baseDn")) {
                            form.setFieldsValue({ baseDn: deriveBaseDn(form.getFieldValue("domain")) });
                            setBaseDnEditable(false);
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row justify="space-between" align="top" style={{ marginTop: "1.5rem" }}>
                  <Col span={11}>
                    <Form.Item
                      className="mb-2"
                      name="enforcementMode"
                      label="Enforcement Mode"
                      rules={[{ required: true, message: "Enforcement Mode is required" }]}
                    >
                      <Radio.Group>
                        <RadioChoice
                          value="monitor"
                          title="Monitor"
                          hint="Logs authentication events without blocking anything. Start here."
                        />
                        <RadioChoice
                          value="enforce"
                          title="Enforce"
                          hint="Applies MFA verdicts. Users may be blocked."
                        />
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col span={11}>
                    <Form.Item
                      className="mb-2"
                      name="fallbackMode"
                      label="Fallback if Authnull is unreachable"
                      rules={[{ required: true, message: "Fallback behaviour is required" }]}
                    >
                      <Radio.Group>
                        <RadioChoice
                          value="allow"
                          title="Allow"
                          hint="Logins continue without MFA during an outage (recommended)."
                        />
                        <RadioChoice
                          value="deny"
                          title="Deny"
                          hint="Logins are refused during an outage."
                        />
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>

                <Row style={{ marginTop: "1.5rem" }}>
                  <Col span={24}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>Sync scope</div>
                    <Space size={8} align="center" style={{ marginTop: 2 }}>
                      <span style={{ fontSize: 12.5, color: AN_COLORS.muted }}>
                        All users in this domain
                      </span>
                      <Button
                        type="link"
                        size="small"
                        className="!p-0 !h-auto"
                        onClick={() => {
                          if (scopeNarrowed) {
                            form.setFieldsValue({ syncFilterGroups: [], syncFilterOUs: [] });
                          }
                          setScopeNarrowed((v) => !v);
                        }}
                      >
                        {scopeNarrowed ? "Reset to all users" : "Narrow scope"}
                      </Button>
                    </Space>
                  </Col>
                </Row>

                <Row
                  justify="space-between"
                  align="middle"
                  style={{ marginTop: 12, display: scopeNarrowed ? undefined : "none" }}
                >
                  <Col span={11}>
                    <Form.Item
                      className="mb-2"
                      name="syncFilterGroups"
                      label={
                        <Space size={4}>
                          <span>Sync Filter — Groups</span>
                          <Tooltip title="Plain group names (no CN= prefix). Press Enter or comma to add.">
                            <InfoCircleOutlined />
                          </Tooltip>
                        </Space>
                      }
                      rules={[{ validator: validateGroupChips }]}
                    >
                      <Select
                        mode="tags"
                        tokenSeparators={[","]}
                        open={false}
                        placeholder="e.g. Domain Admins, VPN-Users"
                        suffixIcon={null}
                        className="ad-tag-input"
                      />
                    </Form.Item>
                  </Col>
                  <Col span={11}>
                    <Form.Item
                      className="mb-2"
                      name="syncFilterOUs"
                      label={
                        <Space size={4}>
                          <span>Sync Filter — OUs</span>
                          <Tooltip title="Each value must start with OU=. Press Enter or comma to add.">
                            <InfoCircleOutlined />
                          </Tooltip>
                        </Space>
                      }
                      rules={[{ validator: validateOuChips }]}
                    >
                      <Select
                        mode="tags"
                        tokenSeparators={[","]}
                        open={false}
                        placeholder="e.g. OU=AdminUsers, OU=ITStaff"
                        suffixIcon={null}
                        className="ad-tag-input"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row style={{ marginTop: "1.5rem" }} justify="end" align="middle">
                  <Col>
                    <Button
                      style={{ marginRight: "1rem" }}
                      onClick={() => navigate(-1)}
                    >
                      Cancel
                    </Button>
                    <Button
                      id="nextBtn"
                      type="primary"
                      htmlType="submit"
                      loading={isDataLoading}
                    >
                      Save & Continue
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  return (
    <div
      style={{
        minHeight: 'calc(100vh - 64px)',
        background: AN_COLORS.bgWarm,
        color: AN_COLORS.ink,
        fontFamily: AN_FONT,
        padding: '22px 28px',
      }}
    >
      {setNotification}

      <div style={{ marginBottom: 18 }}>
        <div
          style={{
            fontFamily: AN_MONO,
            fontSize: 10.5,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
            color: AN_COLORS.muted,
            marginBottom: 6,
          }}
        >
          Admin · Directory · Active Directory
        </div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: -0.4 }}>
          Add Active Directory
        </h1>
        <div style={{ fontSize: 13, color: AN_COLORS.muted, marginTop: 4 }}>
          Connect a Domain Controller to sync users and groups into AuthNull.
        </div>
      </div>

      <Steps
        current={current}
        style={{ marginBottom: 24 }}
        items={[
          { title: "Connect Active Directory" },
          { title: "Install DC Sensor" },
        ]}
      />

      <div
        style={{
          background: 'var(--an-surface)',
          border: `1px solid ${AN_COLORS.line}`,
          borderRadius: 10,
          padding: 20,
        }}
      >
        {current === 0 && <ActiveDirectory />}
        {current === 1 && (
          <GatewayInstall
            defaultDomainId={adId}
            domainOptions={
              adId
                ? [{
                    id: adId,
                    directoryName: formVal?.directoryName,
                    domain: formVal?.domain,
                  }]
                : []
            }
            enforcementMode={formVal?.enforcementMode}
            fallbackMode={formVal?.fallbackMode}
            lockDomain
            doneTo="/pam/directory"
          />
        )}
      </div>
    </div>
  );
}
